/**
 * useWeatherApi - Weather data fetching and bidirectional format conversion
 *
 * This hook provides:
 * 1. Weather data fetching from Node.js backend service
 * 2. Bidirectional mapping between Frontend (FE) and Backend (BE) formats
 *
 * Key Functions:
 * - useWeatherApi() - React hook for fetching weather data
 * - mapWeather(data, direction) - Bidirectional converter ('FE_TO_BE' or 'BE_TO_FE')
 * - mapWeatherToBackend(data) - Legacy FE -> BE converter (for saving)
 *
 * Format Differences:
 * - Sky Condition: Both UI and BE use text-based values ('Clear, cloudless sky', 'Partly cloudy', etc.)
 * - Temperature Unit: UI uses '°F'/'°C', BE uses 'F'/'C'
 * - Wind Speed Unit: UI may use 'MPH', BE uses 'mph'
 * - Numbers: UI uses strings, BE uses numbers
 *
 * @see APPLICATION_WEATHER_VALIDATION_REFERENCE.md for complete validation rules
 */

import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import axios from 'axios';
import * as turf from '@turf/turf';
import { Context } from 'components/Store';
import { getAccessToken } from 'utilities/base-auth';
import { WEATHER_ERROR_TYPES } from 'screens/Applications&WorkOrders/StepsLayout/Helpers/weatherErrorModal';
import { roundWeatherValue } from 'helpers/weatherDisplayHelpers';

// Default weather data with empty values for future dates or error scenarios
export const defaultWeatherData = {
  skyCondition: '',
  temperature: '',
  humidity: '',
  windSpeed: '',
  windDirection: '',
  soilMoisture: '',
  temperatureUnit: '',
  humidityUnit: '',
  windSpeedUnit: '',
  temperatureSelectedUnit: '°F',
  windSpeedSelectedUnit: 'MPH',
  manuallyModifiedFields: []
};

// ==================== VALIDATION HELPERS ====================

/**
 * Validates if a date can be parsed correctly
 * @param {Date} parsedDate - The parsed date object to validate
 * @returns {boolean} - True if the date is valid, false otherwise
 */
export const isValidParsedDate = parsedDate => {
  return !Number.isNaN(parsedDate.getTime());
};

/**
 * Checks if weather API fetch params are valid
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {boolean} - True if all params are valid, false otherwise
 */
export const isValidWeatherFetchParams = (
  latitude,
  longitude,
  startDate,
  endDate
) => {
  return !!(latitude != null && longitude != null && startDate && endDate);
};

/**
 * Checks if weather data object has meaningful content
 * @param {Object} weatherData - Weather data object to check
 * @returns {boolean} - True if data has properties, false if empty
 */
export const hasWeatherData = weatherData => {
  return !!(weatherData && Object.keys(weatherData).length > 0);
};

// Helper function to create appropriate turf geometry based on type
const createTurfGeometry = geom => {
  return geom.type === 'MultiPolygon'
    ? turf.multiPolygon(geom.coordinates)
    : turf.polygon(geom.coordinates);
};

/**
 * Extracts unique field geometries from crop zones.
 *
 * Each crop zone carries `fieldGeometry` — an array of GeoJSON features
 * representing the parent field(s), set by the SelectPropertiesDrawer.
 * Multiple crop zones can share the same field, so we deduplicate by fieldId.
 *
 * @param {Array} zones - Crop zone objects from formik values
 * @returns {Map<string, Object>} Map of unique id → geometry
 */
const extractUniqueGeometries = zones => {
  const geometriesMap = new Map();

  zones.forEach(zone => {
    if (!zone.fieldGeometry?.length) return;

    zone.fieldGeometry.forEach(feature => {
      const fieldId = feature.properties?.id;
      const { geometry } = feature;

      if (
        fieldId &&
        !geometriesMap.has(fieldId) &&
        geometry?.coordinates &&
        (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')
      ) {
        geometriesMap.set(fieldId, geometry);
      }
    });
  });

  return geometriesMap;
};

/**
 * Calculates the centroid coordinates from crop zones' field geometries.
 *
 * Uses field-level geometry (from fieldGeometry) to compute a single
 * [longitude, latitude] centroid for the weather API request.
 *
 * @param {Array} zones - Crop zone objects with fieldGeometry
 * @returns {Array|null} [longitude, latitude] or null if no valid geometries
 */
const calculateCentroid = zones => {
  if (!zones?.length) {
    throw new Error('No crop zones provided for centroid calculation');
  }

  const geometriesMap = extractUniqueGeometries(zones);
  const geometries = Array.from(geometriesMap.values());

  if (geometries.length === 0) {
    throw new Error(
      'No valid field geometries found — fieldGeometry may not be populated yet'
    );
  }

  const features = geometries.map(geom => createTurfGeometry(geom));
  const collection =
    features.length === 1 ? features[0] : turf.featureCollection(features);
  return turf.centroid(collection).geometry.coordinates;
};

// Helper function to convert various date formats to API-compatible ISO string
// Weather service expects format: YYYY-MM-DDTHH:mm:ss (in UTC, without timezone)
const convertDateToApiFormat = date => {
  if (!date) return null;

  let parsedDate;

  // Convert different date types to Date object
  if (typeof date === 'string') {
    parsedDate = new Date(date);
  } else if (date.format && typeof date.format === 'function') {
    // Moment object
    parsedDate = date.toDate();
  } else if (date instanceof Date) {
    parsedDate = new Date(date);
  } else {
    // Try to parse as Date if it's something else
    parsedDate = new Date(date);
  }

  // Validate the parsed date
  if (!isValidParsedDate(parsedDate)) {
    return null;
  }

  // CRITICAL: Get UTC components first (this handles timezone conversion)
  const utcYear = parsedDate.getUTCFullYear();
  const utcMonth = parsedDate.getUTCMonth();
  const utcDate = parsedDate.getUTCDate();
  const utcHours = parsedDate.getUTCHours();
  const utcMinutes = parsedDate.getUTCMinutes();

  // Create a new Date in UTC timezone
  const utcParsedDate = new Date(
    Date.UTC(utcYear, utcMonth, utcDate, utcHours, utcMinutes, 0, 0)
  );

  // Return in the format: YYYY-MM-DDTHH:mm:ss (19 characters, no timezone)
  return utcParsedDate.toISOString().substring(0, 19);
};

// Helper function to map backend weather response to frontend format
const mapWeatherResponse = weatherDTO => {
  if (!weatherDTO) return null;

  return {
    temperature: roundWeatherValue(weatherDTO.temperature),
    temperatureUnit: weatherDTO.temperatureUnit || 'C',
    humidity: roundWeatherValue(weatherDTO.relativeHumidity),
    humidityUnit: 'pct',
    windSpeed: roundWeatherValue(weatherDTO.windSpeed),
    windSpeedUnit: weatherDTO.windSpeedUnit || 'm/s',
    windDirection: weatherDTO.windDirection,
    soilMoisture: weatherDTO.soilMoisture,
    skyCondition: weatherDTO.skyCondition
  };
};

// ==================== WEATHER ADAPTER FOR BACKEND API ====================
// Based on APPLICATION_WEATHER_VALIDATION_REFERENCE.md
// Provides bidirectional mapping between Frontend (FE) and Backend (BE) formats

/**
 * Adapter constants for backend validation
 */
const WEATHER_ADAPTER = {
  WIND_SPEED_UNITS: ['mph', 'km/h', 'm/s'],
  TEMPERATURE_UNITS: ['C', 'F'],
  WIND_DIRECTIONS: [
    'N',
    'NE',
    'NNE',
    'NW',
    'NNW',
    'S',
    'SE',
    'SSE',
    'SW',
    'SSW',
    'E',
    'ENE',
    'ESE',
    'W',
    'WSW',
    'WNW',
    'Unknown',
    'Calm',
    'LW',
    'LB',
    'Windy',
    'Breezy',
    'Gusty',
    'Variable Winds'
  ],
  SOIL_MOISTURE_LEVELS: ['Dry', 'Moist', 'Wet', 'Unknown'],
  SKY_CONDITIONS: [
    'Clear and few clouds',
    'Clear, cloudless sky',
    'Cloudy with thunderstorms and heavy showers',
    'Fog',
    'Mixed with showers',
    'Mixed with snow showers',
    'Mostly clear with a chance of local thunderstorms',
    'Mostly cloudy',
    'Mostly cloudy with a mixture of snow and rain',
    'Mostly cloudy with occasional rain',
    'Mostly cloudy with occasional snow',
    'Mostly cloudy with rain',
    'Mostly cloudy with snow',
    'Mostly cloudy with thunderstorms and showers',
    'Overcast',
    'Overcast with occasional rain',
    'Overcast with occasional snow',
    'Overcast with rain',
    'Overcast with snow',
    'Partly cloudy',
    'Partly cloudy with a chance of local thunderstorms',
    'Partly cloudy with local thunderstorms and showers possible',
    'Showers, thunderstorms likely',
    'Unknown'
  ]
};

/**
 * Clean temperature unit - Remove degree symbol (°F → F, °C → C)
 */
const cleanTemperatureUnit = unit => {
  if (!unit) return undefined;
  const cleaned = unit
    .replace('°', '')
    .trim()
    .toUpperCase();
  // Validate against allowed values
  return WEATHER_ADAPTER.TEMPERATURE_UNITS.includes(cleaned)
    ? cleaned
    : undefined;
};

/**
 * Clean wind speed unit - Normalize to backend format (MPH → mph, MPS → m/s, KMH → km/h)
 */
const cleanWindSpeedUnit = unit => {
  if (!unit) return undefined;
  const upperUnit = unit.toUpperCase().trim();

  // Map uppercase frontend units to backend format
  if (upperUnit === 'MPH') return 'mph';
  if (upperUnit === 'MPS' || upperUnit === 'M/S') return 'm/s';
  if (upperUnit === 'KMH' || upperUnit === 'KM/H') return 'km/h';

  // If already in backend format, return as-is
  const cleaned = unit.toLowerCase().trim();
  const match = WEATHER_ADAPTER.WIND_SPEED_UNITS.find(
    validUnit => validUnit.toLowerCase() === cleaned
  );

  return match;
};

/**
 * Normalize wind direction - Match backend enum
 */
const normalizeWindDirection = direction => {
  if (!direction) return undefined;

  // Find matching direction (case-insensitive)
  const match = WEATHER_ADAPTER.WIND_DIRECTIONS.find(
    validDir => validDir.toUpperCase() === direction.toUpperCase()
  );

  return match;
};

/**
 * Normalize soil moisture - Defaults to "Unknown" if invalid (backend behavior)
 */
const normalizeSoilMoisture = moisture => {
  if (!moisture) return 'Unknown';

  // Find matching level (case-insensitive)
  const match = WEATHER_ADAPTER.SOIL_MOISTURE_LEVELS.find(
    validLevel => validLevel.toLowerCase() === moisture.toLowerCase()
  );

  return match || 'Unknown'; // Default to Unknown like backend
};

/**
 * Normalize sky condition - Match backend enum
 */
const normalizeSkyCondition = condition => {
  if (!condition) return undefined;

  // Find matching condition (case-insensitive)
  const match = WEATHER_ADAPTER.SKY_CONDITIONS.find(
    validCond => validCond.toLowerCase() === condition.toLowerCase()
  );

  return match;
};

/**
 * Validate and normalize sky condition (FE ↔ BE use same text values)
 * @param {string} condition - Sky condition text
 * @returns {string|undefined} - Validated sky condition or undefined
 */
const validateSkyCondition = condition => {
  if (!condition) return undefined;
  return WEATHER_ADAPTER.SKY_CONDITIONS.find(
    c => c.toLowerCase() === condition.toLowerCase()
  );
};

// Alias functions for backwards compatibility
const convertPictoCodeToText = validateSkyCondition;
const convertTextToPictoCode = validateSkyCondition;

/**
 * Bidirectional weather data converter
 * @param {Object|undefined} weatherData - Weather data to convert
 * @param {string} direction - Conversion direction: 'FE_TO_BE' or 'BE_TO_FE'
 * @returns {Object|undefined} - Converted weather object or undefined if no data
 */
export const mapWeather = (weatherData, direction = 'FE_TO_BE') => {
  if (!weatherData) return undefined;

  if (direction === 'FE_TO_BE') {
    return mapWeatherFeToBe(weatherData);
  }
  if (direction === 'BE_TO_FE') {
    return mapWeatherBeToFe(weatherData);
  }

  throw new Error(
    `Invalid direction: ${direction}. Use 'FE_TO_BE' or 'BE_TO_FE'`
  );
};

/**
 * Convert frontend weather data to backend ApplicationVO format (FE -> BE)
 * Used when saving applications
 * @param {Object} formWeatherData - Weather data from Formik form
 * @returns {Object|undefined} - Backend-compatible weather object or undefined if no data
 */
// Helper: Check if value is not empty
const hasValue = val => val !== undefined && val !== null && val !== '';

// Helper: Map a single weather period (FE -> BE)
const mapWeatherPeriod = periodData => {
  const mapped = {
    ...(hasValue(periodData.windSpeed) && {
      windSpeed: Number(periodData.windSpeed)
    }),
    ...((periodData.windSpeedSelectedUnit || periodData.windSpeedUnit) && {
      windSpeedUnit: cleanWindSpeedUnit(
        periodData.windSpeedSelectedUnit || periodData.windSpeedUnit
      )
    }),
    // Always include windDirection to allow clearing (send null when empty)
    windDirection: periodData.windDirection
      ? normalizeWindDirection(periodData.windDirection)
      : null,
    // Always include skyCondition to allow clearing (send null when empty)
    skyCondition: periodData.skyCondition
      ? convertPictoCodeToText(periodData.skyCondition) ||
        normalizeSkyCondition(periodData.skyCondition)
      : null,
    ...(hasValue(periodData.temperature) && {
      temperature: Number(periodData.temperature)
    }),
    ...((periodData.temperatureSelectedUnit || periodData.temperatureUnit) && {
      temperatureUnit: cleanTemperatureUnit(
        periodData.temperatureSelectedUnit || periodData.temperatureUnit
      )
    }),
    // Always include soilMoisture to allow clearing (send null when empty)
    soilMoisture: periodData.soilMoisture
      ? normalizeSoilMoisture(periodData.soilMoisture)
      : null,
    ...(hasValue(periodData.humidity) && {
      humidity: Number(periodData.humidity)
    }),
    manuallyModifiedFields: periodData.manuallyModifiedFields || []
  };

  // Remove undefined values
  Object.keys(mapped).forEach(key => {
    if (mapped[key] === undefined) delete mapped[key];
  });

  return Object.keys(mapped).length > 0 ? mapped : null;
};

const mapWeatherFeToBe = formWeatherData => {
  const hasStartData =
    formWeatherData.start &&
    Object.values(formWeatherData.start).some(hasValue);
  const hasEndData =
    formWeatherData.end && Object.values(formWeatherData.end).some(hasValue);

  // Always return start and end objects (even if empty) to preserve structure
  // This ensures the toggle state is saved even without weather data
  const result = {
    start: {},
    end: {}
  };

  if (hasStartData) {
    const mapped = mapWeatherPeriod(formWeatherData.start);
    if (mapped) result.start = mapped;
  }

  if (hasEndData) {
    const mapped = mapWeatherPeriod(formWeatherData.end);
    if (mapped) result.end = mapped;
  }

  return result;
};

/**
 * Convert backend weather data to frontend form format (BE -> FE)
 * Used when loading applications for edit
 * @param {Object} backendWeatherData - Weather data from backend API
 * @returns {Object|undefined} - Frontend-compatible weather object or undefined if no data
 */
// Helper: Convert backend wind speed unit to frontend format
const backendToFrontendWindSpeedUnit = unit => {
  if (!unit) return undefined;
  const lowerUnit = unit.toLowerCase().trim();
  if (lowerUnit === 'm/s' || lowerUnit === 'mps') return 'MPS';
  if (lowerUnit === 'mph') return 'MPH';
  if (lowerUnit === 'km/h' || lowerUnit === 'kmh') return 'KMH';
  return unit;
};

// Helper: Map a single weather period from backend to frontend (BE -> FE)
// Ensures all fields have default empty string values for consistent comparison
const mapWeatherPeriodBeToFe = periodData => {
  return {
    // Always include all fields with default empty values for consistent comparisons
    windSpeed:
      periodData.windSpeed === undefined ? '' : String(periodData.windSpeed),
    windSpeedUnit: periodData.windSpeedUnit
      ? backendToFrontendWindSpeedUnit(periodData.windSpeedUnit)
      : '',
    windSpeedSelectedUnit: periodData.windSpeedUnit
      ? backendToFrontendWindSpeedUnit(periodData.windSpeedUnit)
      : '',
    windDirection: periodData.windDirection ?? '',
    skyCondition: periodData.skyCondition
      ? convertTextToPictoCode(periodData.skyCondition) ||
        periodData.skyCondition
      : '',
    temperature:
      periodData.temperature === undefined
        ? ''
        : String(periodData.temperature),
    temperatureUnit: periodData.temperatureUnit
      ? `°${periodData.temperatureUnit}`
      : '',
    temperatureSelectedUnit: periodData.temperatureUnit
      ? `°${periodData.temperatureUnit}`
      : '',
    soilMoisture: periodData.soilMoisture ?? '',
    humidity:
      periodData.humidity === undefined ? '' : String(periodData.humidity),
    humidityUnit: periodData.humidity === undefined ? '' : '%',
    manuallyModifiedFields: periodData.manuallyModifiedFields || []
  };
};

const mapWeatherBeToFe = backendWeatherData => {
  const hasStartData = hasWeatherData(backendWeatherData.start);
  const hasEndData = hasWeatherData(backendWeatherData.end);

  if (!hasStartData && !hasEndData) return undefined;

  const result = {};

  if (hasStartData) {
    result.start = mapWeatherPeriodBeToFe(backendWeatherData.start);
  }

  if (hasEndData) {
    result.end = mapWeatherPeriodBeToFe(backendWeatherData.end);
  }

  return result;
};

/**
 * Legacy export for backward compatibility
 * Convert frontend weather data to backend ApplicationVO format
 * @param {Object|undefined} formWeatherData - Weather data from Formik form (can be undefined)
 * @returns {Object|undefined} - Backend-compatible weather object or undefined if no data
 */
export const mapWeatherToBackend = formWeatherData => {
  if (!formWeatherData) return undefined;
  return mapWeatherFeToBe(formWeatherData);
};

const useWeatherApi = () => {
  const [{ organization }] = useContext(Context);

  const [weatherData, setWeatherData] = useState({
    startDate: null,
    endDate: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiErrorValue, setApiErrorValue] = useState(null);
  const cancelTokensRef = useRef(new Map()); // Use Map to store multiple cancel tokens

  // Fetch weather data - makes a single API call and gets both start and end weather
  const fetchWeatherData = useCallback(
    async (latitude, longitude, startDate, endDate) => {
      if (!isValidWeatherFetchParams(latitude, longitude, startDate, endDate))
        return;

      // Cancel previous request if it exists
      if (cancelTokensRef.current.has('weather')) {
        cancelTokensRef.current
          .get('weather')
          .cancel('Request cancelled due to new request');
      }

      // Create new cancel token
      const cancelTokenSource = axios.CancelToken.source();
      cancelTokensRef.current.set('weather', cancelTokenSource);

      try {
        // Set loading state to true
        setLoading(true);
        setError(null);

        const apiHost = "https://api-v2-prod.financials.cropwise.com";
        const baseUrl = `${apiHost}/weather/specific`;

        const params = {
          latitude,
          longitude,
          startDate,
          endDate
        };

        const response = await axios.get(baseUrl, {
          params,
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            'cwf-context': JSON.stringify({ organization_id: organization.id })
          },
          cancelToken: cancelTokenSource.token
        });

        // Map the backend response to frontend format
        // Backend returns BOTH weather objects in a single response: { startDate: WeatherDTO, endDate?: WeatherDTO }
        const mappedStartDate = mapWeatherResponse(response.data.startDate);
        const mappedEndDate = response.data.endDate
          ? mapWeatherResponse(response.data.endDate)
          : null;

        // Update BOTH weather fields at once from the single API response
        setWeatherData({
          startDate: mappedStartDate,
          endDate: mappedEndDate
        });

        // Remove the completed request from the map
        cancelTokensRef.current.delete('weather');
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err.message || 'Failed to fetch weather data');
          // Set API error type and default weather values
          setApiErrorValue(WEATHER_ERROR_TYPES.WEATHER_API_ERROR);
          setWeatherData({
            startDate: { ...defaultWeatherData },
            endDate: { ...defaultWeatherData }
          });
        }
        // Remove the cancelled/errored request from the map
        cancelTokensRef.current.delete('weather');
      } finally {
        // Set loading state to false
        setLoading(false);
      }
    },
    [organization.id]
  );

  // Cleanup on unmount
  useEffect(() => {
    const cancelTokens = cancelTokensRef.current;
    return () => {
      // Cancel all active requests
      cancelTokens.forEach((cancelTokenSource, dateType) => {
        cancelTokenSource.cancel(
          `Component unmounted - cancelling ${dateType} request`
        );
      });
      cancelTokens.clear();
    };
  }, []);

  // Fetch weather for both start and end dates in a single API call
  const fetchWeatherForDate = useCallback(
    (cropZones, startDate, endDate) => {
      if (cropZones.length && startDate && endDate) {
        let centroidCoordinates;

        try {
          centroidCoordinates = calculateCentroid(cropZones);
          // Clear any previous API error on success
          setApiErrorValue(null);
        } catch {
          // Set API error type and default weather values
          setApiErrorValue(WEATHER_ERROR_TYPES.CALC_CENTROID_ERROR);
          setWeatherData({
            startDate: { ...defaultWeatherData },
            endDate: { ...defaultWeatherData }
          });
          return;
        }

        // Extract latitude and longitude from centroid [longitude, latitude]
        const [longitude, latitude] = centroidCoordinates || [];

        const convertedStartDate = convertDateToApiFormat(startDate);
        const convertedEndDate = convertDateToApiFormat(endDate);

        if (
          latitude != null &&
          longitude != null &&
          convertedStartDate &&
          convertedEndDate
        ) {
          // Single API call fetches BOTH start and end weather data
          fetchWeatherData(
            latitude,
            longitude,
            convertedStartDate,
            convertedEndDate
          );
        }
      }
    },
    [fetchWeatherData]
  );

  // Clear API error (called after modal is shown)
  const clearApiError = useCallback(() => {
    setApiErrorValue(null);
  }, []);

  return {
    weatherData,
    loading,
    error,
    apiErrorValue,
    clearApiError,
    fetchWeatherForDate
  };
};

export default useWeatherApi;
